import React, { useMemo } from 'react'
import { useTheme } from '@mui/material'
import { getNoConflicts, getShowDivider } from 'components/AbsenceRequestConflicts'
import useFavurTranslation from 'hooks/useFavurTranslation'
import Divider from 'redesign/components/atoms/Divider'
import { AbsenceFullDayIcon, AbsencePlanIcon, AlertTriangleIcon } from 'redesign/components/atoms/Icon'
import Alert from 'redesign/components/atoms/alert'
import HeaderBlock from 'redesign/components/molecules/HeaderBlock'
import type { Person } from 'shared/graphql/graphql'
import ConflictList from './conflict-list'
import { REQUEST_COUNT } from './constants'
import { Container } from './styles'
import type { AbsenceRequestConflictsProps } from './types'

const AbsenceRequestConflicts: React.FC<AbsenceRequestConflictsProps> = ({ conflicts, onClick }) => {
  const { palette } = useTheme()
  const { t } = useFavurTranslation()
  const { vacationsCount, otherLeavesCount, pendingRequestsCount, vacationsPersons } = conflicts
  const showDivider = getShowDivider(vacationsCount, otherLeavesCount, pendingRequestsCount)
  const noConflicts = useMemo(
    () => getNoConflicts(vacationsCount, otherLeavesCount, pendingRequestsCount),
    [otherLeavesCount, pendingRequestsCount, vacationsCount],
  )

  if (noConflicts) return null

  return (
    <Alert
      severity="info"
      icon={false}
      titlePadding
      onClick={onClick}
      title={
        <HeaderBlock variant="subtitle2" title={t('component.absenceRequestConflicts.header.title')} showChevron />
      }
      body={
        <Container>
          <ConflictList
            count={vacationsCount}
            icon={<AlertTriangleIcon color={palette.warning.main} />}
            label={
              vacationsCount === REQUEST_COUNT
                ? 'component.absenceRequestConflicts.conflict.vacations'
                : 'component.absenceRequestConflicts.conflicts.vacations'
            }
            showAvatars
            persons={(vacationsPersons as Person[]) ?? []}
          />

          {showDivider && <Divider fullWidth fullSize />}

          <ConflictList
            count={otherLeavesCount}
            icon={<AbsenceFullDayIcon color={palette.text.primary} />}
            label={
              otherLeavesCount === REQUEST_COUNT
                ? 'component.absenceRequestConflicts.conflict.otherLeaves'
                : 'component.absenceRequestConflicts.conflicts.otherLeaves'
            }
          />

          <ConflictList
            count={pendingRequestsCount}
            icon={<AbsencePlanIcon color={palette.text.primary} />}
            label={
              pendingRequestsCount === REQUEST_COUNT
                ? 'component.absenceRequestConflicts.conflict.pendingRequests'
                : 'component.absenceRequestConflicts.conflicts.pendingRequests'
            }
          />
        </Container>
      }
    ></Alert>
  )
}

export default AbsenceRequestConflicts
